import { useState, useEffect } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { 
  Send, Mail, CheckCircle2, ExternalLink, Plus, X, User, 
  Users, Edit3, Link2, AlertCircle, Briefcase, UserPlus, MailX
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { Switch } from "../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import StructuredEmailEditor from "./StructuredEmailEditor";

// Default calendar: "Production Planning Call", which replaced the client
// kickoff call (NYW4...) on 2026-10-26. The field and payload keep their
// kickoff_* names so stored programs and email logs still line up.
const DEFAULT_KICKOFF_LINK = "https://api.leadconnectorhq.com/widget/booking/Zsq2xCCRkCR7mzk43AmY";

// PM Responsibility options
const PM_RESPONSIBILITIES = [
  { value: "visualization", label: "Visualization", color: "bg-blue-100 text-blue-800" },
  { value: "marketing", label: "Marketing", color: "bg-pink-100 text-pink-800" },
  { value: "production", label: "Production", color: "bg-amber-100 text-amber-800" },
  { value: "lead", label: "Lead PM", color: "bg-purple-100 text-purple-800" },
  { value: "other", label: "Other", color: "bg-gray-100 text-gray-800" },
];

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// Shape the editor expects before the server preview lands. The real template
// state lives on the page (see the props below), so this is only a placeholder.
const EMPTY_EMAIL_TEMPLATE = {
  subject: "",
  greeting: "",
  intro: "",
  cta_label: "Book Your Production Planning Call",
  cta_url_mode: "dynamic",
  cta_override_url: "",
  body_sections: [],
  checklist_items: [],
  signature: {
    name: "",
    title: "",
    company: "The Render Pros",
    phone: "",
    email: ""
  },
  custom_html: null
};

export default function ActivationActivateTab({ 
  program, 
  checklist,
  setChecklist,
  kickoffLink: parentKickoffLink,
  setKickoffLink: parentSetKickoffLink,
  customMessage,
  setCustomMessage,
  // Everything below is held by the page rather than here. The activation
  // dialog's tabs unmount when you switch away from them, so any choice kept in
  // this component's own state is silently thrown away and reverts to its
  // default on the way back — which is how a project got activated with a
  // kickoff email after the PM had turned "Skip Welcome Email" on and stepped
  // over to the Check tab to finish the checklist.
  skipKickoffEmail,
  setSkipKickoffEmail,
  assignedPMs,
  setAssignedPMs,
  additionalRecipients,
  setAdditionalRecipients,
  emailTemplate: parentEmailTemplate,
  setEmailTemplate,
  onActivate, 
  activating 
}) {
  // Use parent kickoff link state, default to Bernard's link
  const kickoffLink = parentKickoffLink || DEFAULT_KICKOFF_LINK;
  const setKickoffLink = parentSetKickoffLink || (() => {});
  
  const [emailPreview, setEmailPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  
  // PM Assignment state - multiple PMs with responsibilities (list lifted to the page)
  const [pmUsers, setPmUsers] = useState([]);
  const [showAddPM, setShowAddPM] = useState(false);
  const [selectedPMId, setSelectedPMId] = useState("");
  const [selectedResponsibility, setSelectedResponsibility] = useState("visualization");
  
  // Structured email template — null until the preview seeds it, or the page
  // hands back what the user already edited.
  const emailTemplate = parentEmailTemplate || EMPTY_EMAIL_TEMPLATE;
  
  // Recipients management (the list itself is lifted to the page)
  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [newRecipientName, setNewRecipientName] = useState("");
  const [newRecipientEmail, setNewRecipientEmail] = useState("");
  const [users, setUsers] = useState([]);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  
  // Set default kickoff link on mount
  useEffect(() => {
    if (!parentKickoffLink && parentSetKickoffLink) {
      parentSetKickoffLink(DEFAULT_KICKOFF_LINK);
    }
  }, []);
  
  // Fetch email preview and PM users when component mounts or program changes
  useEffect(() => {
    if (program?.id) {
      fetchEmailPreview();
      fetchUsers();
      fetchPMUsers();
    }
  }, [program?.id]);
  
  // Fetch PM/COO users for assignment
  async function fetchPMUsers() {
    try {
      const response = await apiClient.get("/users");
      const pms = (response.data || []).filter(u => 
        u.role === "Program Manager" || u.role === "Marketing PM" || u.role === "COO"
      );
      setPmUsers(pms);
    } catch (error) {
      console.error("Failed to load PM users:", error);
    }
  }
  
  // Initialize template from preview. Seed only while the page still holds
  // nothing: the preview refetches on every remount of this tab, so without the
  // guard, switching tabs would overwrite the user's edits with the server copy.
  useEffect(() => {
    if (emailPreview?.template && !parentEmailTemplate) {
      setEmailTemplate(emailPreview.template);
    }
  }, [emailPreview, parentEmailTemplate]);
  
  async function fetchEmailPreview() {
    setLoadingPreview(true);
    try {
      const response = await apiClient.get(`/programs/${program.id}/kickoff-email-preview`);
      setEmailPreview(response.data);
    } catch (error) {
      console.error("Failed to load email preview:", error);
    } finally {
      setLoadingPreview(false);
    }
  }
  
  async function fetchUsers() {
    try {
      const response = await apiClient.get("/users");
      setUsers(response.data || []);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  }
  
  function addInternalRecipient(user) {
    if (additionalRecipients.some(r => r.email === user.email)) {
      toast.error("This recipient is already added");
      return;
    }
    setAdditionalRecipients([...additionalRecipients, {
      name: user.name,
      email: user.email,
      type: "internal"
    }]);
    setUserSearchOpen(false);
  }
  
  function addExternalRecipient() {
    if (!newRecipientName.trim() || !newRecipientEmail.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (!isValidEmail(newRecipientEmail)) {
      toast.error("Invalid email format");
      return;
    }
    if (additionalRecipients.some(r => r.email === newRecipientEmail)) {
      toast.error("This recipient is already added");
      return;
    }
    
    setAdditionalRecipients([...additionalRecipients, {
      name: newRecipientName.trim(),
      email: newRecipientEmail.trim().toLowerCase(),
      type: "external"
    }]);
    setNewRecipientName("");
    setNewRecipientEmail("");
    setShowAddRecipient(false);
  }
  
  function removeRecipient(email) {
    setAdditionalRecipients(additionalRecipients.filter(r => r.email !== email));
  }
  
  // PM Assignment functions
  function addPMAssignment() {
    if (!selectedPMId) {
      toast.error("Please select a PM");
      return;
    }
    if (assignedPMs.some(pm => pm.user_id === selectedPMId)) {
      toast.error("This PM is already assigned");
      return;
    }
    const pmUser = pmUsers.find(u => u.id === selectedPMId);
    if (!pmUser) return;
    
    setAssignedPMs([...assignedPMs, {
      user_id: selectedPMId,
      name: pmUser.name,
      role: pmUser.role,
      responsibility: selectedResponsibility
    }]);
    setSelectedPMId("");
    setSelectedResponsibility("visualization");
    setShowAddPM(false);
  }
  
  function removePMAssignment(userId) {
    setAssignedPMs(assignedPMs.filter(pm => pm.user_id !== userId));
  }
  
  function handleActivate() {
    if (!skipKickoffEmail && !kickoffLink) {
      toast.error("Please enter a planning call booking link");
      return;
    }
    
    // Update template with current kickoff link
    const finalTemplate = {
      ...emailTemplate,
      cta_url_mode: emailTemplate.cta_url_mode || "dynamic"
    };
    
    // Build activation payload with structured email template and multi-PM assignments
    const activationData = {
      ...checklist,
      kickoff_booking_link: kickoffLink,
      email_template: finalTemplate,
      additional_recipients: additionalRecipients,
      skip_kickoff_email: skipKickoffEmail,
      // Send array of PM assignments for multi-PM support
      assigned_pms: assignedPMs.map(pm => ({
        user_id: pm.user_id,
        responsibility: pm.responsibility
      })),
      // Legacy single PM field for backwards compatibility (use first PM if any)
      program_manager_id: assignedPMs.length > 0 ? assignedPMs[0].user_id : null
    };
    
    onActivate(activationData);
  }
  
  const allChecklistComplete = checklist ? Object.values(checklist).every(v => v) : false;
  const clientEmail = program?.client_email || emailPreview?.client_email || "";
  const clientName = emailPreview?.client_name || program?.client_name || "";
  
  // Variables for template preview
  const templateVariables = emailPreview?.variables || {
    client_name: clientName.split(' ')[0] || "Client",
    project_name: program?.program_name || "",
    pm_name: emailPreview?.sales_rep_name || "",
    start_date: program?.start_window || "",
    estimated_delivery_date: program?.end_window || ""
  };

  return (
    <div className="space-y-6">
      {/* Program Manager Assignment - NEW Multi-PM Section */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-600" />
                Assign Program Managers
              </CardTitle>
              <CardDescription>
                Assign one or more PMs/COOs with their responsibilities for this program
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowAddPM(true)}
              className="gap-2"
              data-testid="add-pm-btn"
            >
              <UserPlus className="w-4 h-4" />
              Add PM
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {assignedPMs.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground bg-white/50 rounded-lg border border-dashed">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No PMs assigned yet</p>
              <p className="text-xs">Click "Add PM" to assign managers to this program</p>
            </div>
          ) : (
            <div className="space-y-2">
              {assignedPMs.map((pm, idx) => {
                const responsibilityConfig = PM_RESPONSIBILITIES.find(r => r.value === pm.responsibility);
                return (
                  <div 
                    key={pm.user_id} 
                    className="flex items-center justify-between p-3 bg-white rounded-lg border"
                    data-testid={`assigned-pm-${pm.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{pm.name}</p>
                        <p className="text-xs text-muted-foreground">{pm.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="secondary" 
                        className={responsibilityConfig?.color || "bg-gray-100"}
                      >
                        {responsibilityConfig?.label || pm.responsibility}
                      </Badge>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removePMAssignment(pm.user_id)}
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        data-testid={`remove-pm-${pm.user_id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add PM Dialog */}
      <Dialog open={showAddPM} onOpenChange={setShowAddPM}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Assign Program Manager
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select PM or COO</Label>
              <Select value={selectedPMId} onValueChange={setSelectedPMId}>
                <SelectTrigger data-testid="pm-select">
                  <SelectValue placeholder="Choose a team member..." />
                </SelectTrigger>
                <SelectContent>
                  {pmUsers
                    .filter(u => !assignedPMs.some(pm => pm.user_id === u.id))
                    .map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <span>{user.name}</span>
                          <Badge variant="outline" className="text-xs ml-2">{user.role}</Badge>
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Responsibility</Label>
              <Select value={selectedResponsibility} onValueChange={setSelectedResponsibility}>
                <SelectTrigger data-testid="responsibility-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PM_RESPONSIBILITIES.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={`${r.color} text-xs`}>
                          {r.label}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                What will this PM be responsible for on this project?
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPM(false)}>Cancel</Button>
            <Button onClick={addPMAssignment} disabled={!selectedPMId} data-testid="confirm-add-pm">
              Add PM
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kickoff Link */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Planning Call Booking Link
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            value={kickoffLink}
            onChange={(e) => setKickoffLink(e.target.value)}
            placeholder="https://api.leadconnectorhq.com/widget/booking/..."
            className="h-11"
          />
          <p className="text-xs text-muted-foreground mt-2">
            Default: the Production Planning Call calendar. This will be used in the "Book Your Production Planning Call" button.
          </p>
        </CardContent>
      </Card>
      
      {/* Email Editor */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Welcome Email
                {program?.service_type && (
                  <Badge 
                    variant="outline" 
                    className={
                      program.service_type === "Marketing" 
                        ? "bg-pink-50 text-pink-700 border-pink-200" 
                        : program.service_type === "Both" 
                          ? "bg-purple-50 text-purple-700 border-purple-200"
                          : "bg-blue-50 text-blue-700 border-blue-200"
                    }
                  >
                    {program.service_type} Template
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {program?.service_type === "Marketing" 
                  ? "Marketing-focused email with campaign goals checklist"
                  : program?.service_type === "Both"
                    ? "Combined template for renderings + marketing projects"
                    : "Renderings-focused email with visualization checklist"}
              </CardDescription>
            </div>
            <Button 
              variant={showEmailEditor ? "default" : "outline"}
              size="sm"
              onClick={() => setShowEmailEditor(!showEmailEditor)}
            >
              <Edit3 className="w-4 h-4 mr-1" />
              {showEmailEditor ? "Hide Editor" : "Edit Email"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Recipients Section */}
          <div className="space-y-3">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Users className="w-4 h-4" />
              Recipients
            </Label>
            
            {/* Primary recipient (client) */}
            <div className="flex items-center gap-2 p-2 bg-secondary/50 rounded-md">
              <Badge variant="secondary" className="text-xs">Client</Badge>
              <span className="text-sm font-medium">{clientName}</span>
              <span className="text-sm text-muted-foreground">{clientEmail}</span>
            </div>
            
            {/* Additional recipients */}
            {additionalRecipients.length > 0 && (
              <div className="space-y-2">
                {additionalRecipients.map((recipient, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-secondary/30 rounded-md">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {recipient.type === "internal" ? "Internal" : "CC"}
                      </Badge>
                      <span className="text-sm">{recipient.name}</span>
                      <span className="text-sm text-muted-foreground">{recipient.email}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeRecipient(recipient.email)}
                      className="h-7 w-7 p-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add recipient buttons */}
            <div className="flex gap-2">
              <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <User className="w-3.5 h-3.5 mr-1" />
                    Add Internal
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-64" align="start">
                  <Command>
                    <CommandInput placeholder="Search team members..." />
                    <CommandList>
                      <CommandEmpty>No users found.</CommandEmpty>
                      <CommandGroup>
                        {users.map(user => (
                          <CommandItem 
                            key={user.id}
                            onSelect={() => addInternalRecipient(user)}
                            className="cursor-pointer"
                          >
                            <User className="w-3.5 h-3.5 mr-2" />
                            <span>{user.name}</span>
                            <span className="ml-auto text-xs text-muted-foreground">{user.role}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowAddRecipient(true)}
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add External
              </Button>
            </div>
          </div>
          
          <Separator />
          
          {/* Email Editor or Preview */}
          {showEmailEditor ? (
            <StructuredEmailEditor
              template={emailTemplate}
              onChange={setEmailTemplate}
              kickoffLink={kickoffLink}
              variables={templateVariables}
            />
          ) : (
            /* Compact Preview */
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Subject</Label>
                {emailTemplate.subject !== emailPreview?.template?.subject && (
                  <Badge variant="outline" className="text-xs text-orange-600">Modified</Badge>
                )}
              </div>
              <div className="p-3 bg-secondary/30 rounded-md">
                <p className="text-sm font-medium">
                  {emailTemplate.subject || emailPreview?.default_subject || "Loading..."}
                </p>
              </div>
              
              {/* Button Link Status */}
              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-md space-y-2">
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                    "{emailTemplate.cta_label || 'Book Your Production Planning Call'}" Button
                  </span>
                </div>
                {emailTemplate.cta_url_mode === "override" && emailTemplate.cta_override_url ? (
                  <div className="text-xs space-y-1">
                    <p className="text-orange-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Manually overridden
                    </p>
                    <p className="text-muted-foreground">Original: {kickoffLink || "Not set"}</p>
                    <p className="font-medium">Using: {emailTemplate.cta_override_url}</p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    URL sourced from Planning Call Link field: {kickoffLink || "(not set)"}
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Skip Welcome Email Toggle */}
      <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${skipKickoffEmail ? 'bg-amber-500/10' : 'bg-muted'}`}>
            {skipKickoffEmail ? <MailX className="w-4 h-4 text-amber-600" /> : <Mail className="w-4 h-4 text-muted-foreground" />}
          </div>
          <div>
            <p className="text-sm font-medium">Skip Welcome Email</p>
            <p className="text-xs text-muted-foreground">
              {skipKickoffEmail 
                ? "Email will NOT be sent, and no planning call reminders will follow"
                : "Welcome email (planning call link) will be sent to client upon activation, from the planning call coordinator. When Planning call automation is on, reminders follow until they book"}
            </p>
          </div>
        </div>
        <Switch 
          checked={skipKickoffEmail} 
          onCheckedChange={setSkipKickoffEmail}
        />
      </div>
      
      {/* Activate Button */}
      <Button
        onClick={handleActivate}
        disabled={activating || !allChecklistComplete || (!skipKickoffEmail && !kickoffLink)}
        className="w-full h-12 text-base"
        size="lg"
      >
        {activating ? (
          <>Activating...</>
        ) : skipKickoffEmail ? (
          <>
            <CheckCircle2 className="w-5 h-5 mr-2" />
            Activate Program (No Email)
          </>
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            Activate Program & Send Welcome Email
          </>
        )}
      </Button>
      
      {!allChecklistComplete && (
        <p className="text-sm text-center text-muted-foreground">
          Complete all checklist items in the "Check" tab to enable activation
        </p>
      )}
      
      {/* Add External Recipient Dialog */}
      <Dialog open={showAddRecipient} onOpenChange={setShowAddRecipient}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add External Recipient</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={newRecipientName}
                onChange={(e) => setNewRecipientName(e.target.value)}
                placeholder="John Smith"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newRecipientEmail}
                onChange={(e) => setNewRecipientEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRecipient(false)}>
              Cancel
            </Button>
            <Button onClick={addExternalRecipient}>
              Add Recipient
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
